import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { RegisterPopComponent } from '../../../features/auth/components/register-pop/register-pop';
import { AuthService } from '../../../core/services/auth.service';
import { LanguageService } from '../../../core/services/language.service';
import { CurrencyService, Currency } from '../../../core/services/currency.service';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { NotificationService, Notification } from '../../../core/services/notification.service';

@Component({
  selector: 'app-navbar',
  imports: [CommonModule, RouterLink, RegisterPopComponent, RouterLinkActive, TranslatePipe],
  standalone: true,
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
})
export class Navbar implements OnInit {
  showMobileMenu = false;
  showRegisterPopup = false;
  showLanguageDropdown = false;
  showCurrencyDropdown = false;
  showNotificationsDropdown = false;
  currentLanguage: string = 'ar';
  currentCurrency: string = 'EGP';
  currencies: Currency[] = [];

  // Notifications
  notifications: Notification[] = [];
  unreadCount = 0;
  notificationsLoading = false;
  markingAll = false;

  // Notification modal
  selectedNotification: Notification | null = null;
  notificationDetailLoading = false;

  constructor(
    private host: ElementRef,
    private router: Router,
    private authService: AuthService,
    private languageService: LanguageService,
    private currencyService: CurrencyService,
    private notificationService: NotificationService,
  ) {
    this.languageService.currentLanguage$.subscribe((language) => {
      this.currentLanguage = language;
    });

    this.currencyService.currentCurrency$.subscribe((currency) => {
      this.currentCurrency = currency;
    });

    this.currencies = this.currencyService.getAllCurrencies();
  }

  ngOnInit(): void {
    if (this.isAuthenticated) {
      this.loadNotifications();
    }
  }

  loadNotifications(): void {
    this.notificationsLoading = true;
    this.notificationService.getNotifications(1, 4).subscribe({
      next: (res) => {
        this.notifications = res.data || [];
        this.unreadCount = this.notifications.filter((n) => !n.isRead).length;
        this.notificationsLoading = false;
      },
      error: () => {
        this.notificationsLoading = false;
      },
    });
  }

  toggleNotificationsDropdown(event: Event): void {
    event.stopPropagation();
    this.showNotificationsDropdown = !this.showNotificationsDropdown;
    this.showLanguageDropdown = false;
    this.showCurrencyDropdown = false;

    if (this.showNotificationsDropdown && this.notifications.length === 0) {
      this.loadNotifications();
    }
  }

  markAllAsRead(): void {
    this.markingAll = true;
    this.notificationService.markAllAsRead().subscribe({
      next: () => {
        this.notifications = this.notifications.map((n) => ({ ...n, isRead: true }));
        this.unreadCount = 0;
        this.markingAll = false;
      },
      error: () => {
        this.markingAll = false;
      },
    });
  }

  openNotification(notification: Notification, event: Event): void {
    event.stopPropagation();
    this.selectedNotification = notification;
    this.showNotificationsDropdown = false;

    // جيب التفاصيل الكاملة من API
    this.notificationDetailLoading = true;
    this.notificationService.getNotification(notification._id).subscribe({
      next: (res) => {
        this.selectedNotification = res.data ?? notification;
        this.notificationDetailLoading = false;
      },
      error: () => {
        this.notificationDetailLoading = false;
      },
    });

    // علّم مقروء لو لم يكن كذلك
    if (!notification.isRead) {
      this.notificationService.markAsRead(notification._id).subscribe({
        next: () => {
          notification.isRead = true;
          if (this.selectedNotification && this.selectedNotification._id === notification._id) {
            this.selectedNotification = { ...this.selectedNotification, isRead: true };
          }
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        },
      });
    }
  }

  closeNotificationModal(): void {
    this.selectedNotification = null;
  }

  markOneAsRead(notification: Notification, event: Event): void {
    event.stopPropagation();
    if (notification.isRead) return;

    this.notificationService.markAsRead(notification._id).subscribe({
      next: () => {
        notification.isRead = true;
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      },
    });
  }

  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  get dashboardRoute(): string {
    const role = this.authService.getRole();
    switch (role) {
      case 'system_admin':
        return '/dashboard/system_admin';
      case 'broker_company':
      case 'broker_individual':
        return '/dashboard/real_estate_broker';
      case 'real_estate_developer':
        return '/dashboard/real_estate_developer';
      case 'property_seeker':
        return '/dashboard/property_seeker';
      case 'property_owner':
        return '/dashboard/property_owner';
      case 'owner_portal':
        return '/dashboard/owner_portal';
      default:
        return '/home';
    }
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.authService.clearTokens();
        this.router.navigate(['/']);
      },
      error: (err) => {
        console.error('Logout failed:', err);
        this.authService.clearTokens();
        this.router.navigate(['/']);
      },
    });
  }

  toggleMenu(): void {
    this.showMobileMenu = !this.showMobileMenu;
  }
  closeMenu(): void {
    this.showMobileMenu = false;
  }

  get ariaExpanded(): string {
    return this.showMobileMenu ? 'true' : 'false';
  }

  openRegisterPopup(): void {
    this.showRegisterPopup = true;
  }
  closeRegisterPopup(): void {
    this.showRegisterPopup = false;
  }

  goToFavorites(): void {
    this.router.navigate(['/favorites']);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!this.host.nativeElement.contains(target)) {
      this.closeMenu();
      this.showNotificationsDropdown = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeMenu();
    this.showNotificationsDropdown = false;
    this.closeNotificationModal();
  }

  toggleLanguage(): void {
    const newLanguage = this.currentLanguage === 'ar' ? 'en' : 'ar';
    this.languageService.setLanguage(newLanguage);
    this.showLanguageDropdown = false;
  }

  toggleLanguageDropdown(): void {
    this.showLanguageDropdown = !this.showLanguageDropdown;
    this.showNotificationsDropdown = false;
  }

  selectLanguage(language: 'ar' | 'en'): void {
    this.languageService.setLanguage(language);
    this.showLanguageDropdown = false;
  }

  get languageLabel(): string {
    return this.currentLanguage === 'ar' ? 'English' : 'عربي';
  }
  get currentFlag(): string {
    return this.currentLanguage === 'ar' ? 'eg' : 'sa';
  }

  toggleCurrencyDropdown(): void {
    this.showCurrencyDropdown = !this.showCurrencyDropdown;
  }

  selectCurrency(currencyCode: string): void {
    this.currencyService.setCurrency(currencyCode);
    this.showCurrencyDropdown = false;
  }

  get currentCurrencyInfo(): Currency | undefined {
    return this.currencyService.getCurrencyInfo(this.currentCurrency);
  }

  timeAgo(dateStr: string): string {
    const now = Date.now();
    const diff = now - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  }
}
